// Dashboard multi-timezone clock settings.
//
// Clocks are persisted per user on the backend. localStorage is a per-user cache (keyed by user id)
// for instant dashboard updates; PreferencesContext hydrates from the server on login.

export const CLOCK_STORAGE_KEY = "dashboardClockSettings";
// Dispatched on the window after a save so the dashboard updates without a reload (same-tab); the native
// `storage` event covers cross-tab updates.
export const CLOCK_SETTINGS_EVENT = "dashboard-clock-settings-changed";

// Special timeZone value meaning "use the device's local timezone".
export const LOCAL_TZ_VALUE = "local";

export const MAX_CLOCKS = 3;

// Default view: local time only; other slots off until the user enables them in Settings.
export const DEFAULT_CLOCKS = [
  { id: "clock-1", label: "Local Time", timeZone: LOCAL_TZ_VALUE, enabled: true },
  { id: "clock-2", label: "Australia/Sydney", timeZone: "Australia/Sydney", enabled: false },
  { id: "clock-3", label: "Asia/Kathmandu", timeZone: "Asia/Kathmandu", enabled: false },
];

/** Default display label when the user picks a timezone (full IANA id, or "Local Time"). */
export const defaultLabelForTimeZone = (timeZone) =>
  timeZone === LOCAL_TZ_VALUE ? "Local Time" : timeZone;

export const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem("userDetail");
    if (!raw) return null;
    const detail = JSON.parse(raw);
    const id = detail?.id ?? detail?.userId;
    return id != null ? String(id) : null;
  } catch {
    return null;
  }
};

/** Per-user localStorage key; falls back to the legacy global key when no user is signed in. */
export const getClockStorageKey = (userId = getCurrentUserId()) =>
  userId ? `${CLOCK_STORAGE_KEY}:${userId}` : CLOCK_STORAGE_KEY;

/** Remove cached clocks for the signed-in user (call before clearing userDetail on logout). */
export const clearClockSettingsCache = (userId = getCurrentUserId()) => {
  try {
    localStorage.removeItem(CLOCK_STORAGE_KEY);
    if (userId) {
      localStorage.removeItem(getClockStorageKey(userId));
    }
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(CLOCK_SETTINGS_EVENT));
};

// Curated fallback used only when the browser cannot enumerate IANA zones.
const FALLBACK_TIME_ZONES = [
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Perth",
  "Australia/Adelaide",
  "Asia/Kathmandu",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "America/Los_Angeles",
  "Pacific/Auckland",
  "UTC",
];

/** Full IANA timezone list when the browser supports it, otherwise a curated fallback. */
export const listTimeZones = () => {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      const zones = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(zones) && zones.length > 0) return zones;
    }
  } catch {
    /* fall through to curated list */
  }
  return FALLBACK_TIME_ZONES;
};

/** True when `timeZone` is the local sentinel or a zone the runtime accepts. */
export const isValidTimeZone = (timeZone) => {
  if (timeZone === LOCAL_TZ_VALUE) return true;
  if (typeof timeZone !== "string" || timeZone.trim() === "") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

const sanitizeClock = (raw, index) => {
  if (!raw || typeof raw !== "object") return null;
  const timeZone = isValidTimeZone(raw.timeZone) ? raw.timeZone : LOCAL_TZ_VALUE;
  const label =
    typeof raw.label === "string" && raw.label.trim() !== ""
      ? raw.label.trim().slice(0, 30)
      : defaultLabelForTimeZone(timeZone);
  return {
    id: typeof raw.id === "string" && raw.id ? raw.id : `clock-${index + 1}`,
    label,
    timeZone,
    enabled: raw.enabled !== false,
  };
};

/** Read clocks from localStorage, validating every field; falls back to defaults on any problem. */
export const loadClockSettings = () => {
  try {
    const storageKey = getClockStorageKey();
    let stored = localStorage.getItem(storageKey);

    // One-time migration: legacy global key → current user (only when scoped key is empty).
    const userId = getCurrentUserId();
    if (!stored && userId) {
      const legacy = localStorage.getItem(CLOCK_STORAGE_KEY);
      if (legacy) {
        stored = legacy;
        localStorage.setItem(storageKey, legacy);
        localStorage.removeItem(CLOCK_STORAGE_KEY);
      }
    }

    if (!stored) return DEFAULT_CLOCKS.map((c) => ({ ...c }));
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return DEFAULT_CLOCKS.map((c) => ({ ...c }));
    const cleaned = parsed
      .slice(0, MAX_CLOCKS)
      .map((c, i) => sanitizeClock(c, i))
      .filter(Boolean);
    return cleaned.length > 0 ? cleaned : DEFAULT_CLOCKS.map((c) => ({ ...c }));
  } catch {
    return DEFAULT_CLOCKS.map((c) => ({ ...c }));
  }
};

/** Persist clocks (immutably sanitized, capped at MAX_CLOCKS) and notify listeners. Returns saved value. */
export const saveClockSettings = (clocks) => {
  const safe = (Array.isArray(clocks) ? clocks : [])
    .slice(0, MAX_CLOCKS)
    .map((c, i) => sanitizeClock(c, i))
    .filter(Boolean);
  try {
    localStorage.setItem(getClockStorageKey(), JSON.stringify(safe));
    window.dispatchEvent(new CustomEvent(CLOCK_SETTINGS_EVENT));
  } catch {
    /* localStorage unavailable (private mode / quota) — settings simply do not persist */
  }
  return safe;
};

/**
 * Format `date` as a clock string for the given timeZone. `LOCAL_TZ_VALUE` uses the device zone.
 * Invalid zones fall back to local time so the dashboard never crashes on bad config.
 */
export const formatZonedTime = (date, timeZone, withSeconds = true) => {
  const options = { hour: "2-digit", minute: "2-digit", hour12: true };
  if (withSeconds) options.second = "2-digit";
  if (timeZone && timeZone !== LOCAL_TZ_VALUE) options.timeZone = timeZone;
  try {
    return new Intl.DateTimeFormat("en-US", options).format(date);
  } catch {
    delete options.timeZone;
    return new Intl.DateTimeFormat("en-US", options).format(date);
  }
};

/** Short zone descriptor (e.g. "GMT+10", "GMT+5:45") for display under the time. */
export const zoneShortName = (date, timeZone) => {
  const options = { hour: "2-digit", timeZoneName: "short" };
  if (timeZone && timeZone !== LOCAL_TZ_VALUE) options.timeZone = timeZone;
  try {
    const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
};
